"use client";
import React, { useRef, useState, useEffect } from "react";
import { IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import IosShareIcon from "@mui/icons-material/IosShare";
import DiamondIcon from "@mui/icons-material/Diamond";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import { useGetPropertyImagesQuery } from "../../../redux/api/propertiesApi";

import TemplateLuxury from "./design/TemplateLuxury";
import TemplateMinimal from "./design/TemplateMinimal";
import TemplateBold from "./design/TemplateBold";

const PRESET_TEMPLATES = [
  { id: "luxury",  name: "Luxury",  tag: "Premium",   color: "#d4af37", icon: <DiamondIcon              sx={{ fontSize: 22 }} /> },
  { id: "minimal", name: "Minimal", tag: "Corporate",  color: "#56C662", icon: <CorporateFareIcon        sx={{ fontSize: 22 }} /> },
  { id: "bold",    name: "Bold",    tag: "Hot Deal",   color: "#000000", icon: <LocalFireDepartmentIcon  sx={{ fontSize: 22 }} /> },
];

// ─── Derived scale helper ────────────────────────────────────────────────────
// Returns the CSS scale value so the 1080×1920 template fits inside `containerPx`.
const getScale = (containerPx) => containerPx / 1080;

export default function ImageTemplates({ propertyData, onClose }) {
  const [selectedId, setSelectedId]   = useState("luxury");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing,    setIsSharing]    = useState(false);
  const [previewScale, setPreviewScale] = useState(0.26); // initial guess

  const templateRef   = useRef(null);
  const previewWrap   = useRef(null); // measures the visible preview box

  const currentagency = useSelector(selectCurrentagency);
  const property      = propertyData?.[0];

  const { data: images } = useGetPropertyImagesQuery(property?.propertyid, {
    skip: !property?.propertyid,
  });

  // ── Measure preview container and derive scale ──────────────────────────────
  useEffect(() => {
    const measure = () => {
      if (previewWrap.current) {
        const w = previewWrap.current.offsetWidth;
        setPreviewScale(getScale(w));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (previewWrap.current) ro.observe(previewWrap.current);
    return () => ro.disconnect();
  }, []);

  // ── Close on Escape + lock background scroll while open ─────────────────────
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // ── Render correct template ─────────────────────────────────────────────────
  const renderTemplate = (id) => {
    const props = { property, images, currentagency };
    switch (id) {
      case "luxury":  return <TemplateLuxury  {...props} />;
      case "minimal": return <TemplateMinimal {...props} />;
      case "bold":    return <TemplateBold    {...props} />;
      default:        return null;
    }
  };

  // ── Download via html2canvas ────────────────────────────────────────────────
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob    = await generateBlob();
      const url     = URL.createObjectURL(blob);
      const link    = document.createElement("a");
      link.download = `property-status-${selectedId}.jpg`;
      link.href     = url;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to generate image.");
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Generate canvas → Blob helper (shared by download & share) ────────────
  const generateBlob = async () => {
    await new Promise((r) => setTimeout(r, 600));
    if (!templateRef.current) throw new Error("Template not rendered yet");
    const canvas = await html2canvas(templateRef.current, {
      useCORS: true, allowTaint: false, scale: 1,
      width: 1080, height: 1920, x: 0, y: 0,
      scrollX: 0, scrollY: 0, backgroundColor: null, logging: false,
    });
    return new Promise((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error("Blob failed"))), "image/jpeg", 0.95)
    );
  };

  // ── Share via Web Share API ─────────────────────────────────────────────────
  const handleShare = async () => {
    setIsSharing(true);
    try {
      const blob = await generateBlob();
      const file = new File([blob], `property-status-${selectedId}.jpg`, { type: "image/jpeg" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Property Listing",
          text:  `Check out this property: ${property?.title || ""}`,
        });
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Share failed:", err);
        alert("Sharing is not supported on this device. Try downloading instead.");
      }
    } finally {
      setIsSharing(false);
    }
  };

  if (!property) return null;

  const busy = isDownloading || isSharing;
  const Spinner = () => (
    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
  );

  return createPortal(
    <div
      className="flex items-end sm:items-center justify-center sm:p-6"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000 }}
      role="dialog"
      aria-modal="true"
      aria-label="Create WhatsApp story ad"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal shell: bottom sheet on phones, centered two-column dialog on md+ */}
      <div
        className="relative w-full max-w-5xl bg-white shadow-2xl flex flex-col md:flex-row overflow-hidden
                   h-[100dvh] sm:h-auto sm:max-h-[92dvh] md:h-[min(760px,92dvh)]
                   rounded-none sm:rounded-3xl"
        style={{ animation: "modalIn 0.25s cubic-bezier(.22,1,.36,1) both" }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ position: "absolute", top: 12, right: 12, zIndex: 50, width: 40, height: 40 }}
          className="rounded-full bg-white shadow-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-700 transition"
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </button>

        {/* ── Preview pane ───────────────────────────────────────────────── */}
        <div className="relative flex-shrink-0 md:w-[44%] bg-gradient-to-br from-[#eef7ef] via-[#f4f6f8] to-[#e8f0fb] flex flex-col items-center justify-center px-6 pt-16 pb-4 md:p-10 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-[#56C662]/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-16 w-64 h-64 rounded-full bg-blue-400/20 blur-3xl pointer-events-none" />

          {/* Width is limited by available height so the 9:16 card never overflows */}
          <div
            ref={previewWrap}
            className="relative z-10 aspect-[9/16] rounded-[1.5rem] overflow-hidden bg-white ring-[6px] ring-white shadow-[0_24px_60px_rgba(0,0,0,0.25)]
                       w-[min(100%,calc(32dvh*9/16*1.5))] sm:w-[min(100%,calc(38dvh*9/16*1.5))] md:w-[min(100%,300px,calc(66dvh*9/16))]"
          >
            <div style={{ width: 1080, height: 1920, transformOrigin: "top left", transform: `scale(${previewScale})` }}>
              {renderTemplate(selectedId)}
            </div>
          </div>

          <div className="hidden sm:flex mt-5 relative z-10 items-center gap-1.5 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-sm">
            <AutoAwesomeIcon sx={{ fontSize: 13, color: "#56C662" }} />
            <span className="text-[10px] font-extrabold text-gray-600 uppercase tracking-[0.18em]">Live preview</span>
          </div>
        </div>

        {/* ── Controls pane ──────────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-h-0 bg-white">
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-5 pb-4 sm:px-8 md:px-10 md:pt-14 md:pr-16">
            <span className="inline-flex items-center gap-2 bg-[#56C662]/10 text-[#2f9e3d] px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-[0.18em]">
              <span className="w-1.5 h-1.5 bg-[#56C662] rounded-full animate-pulse" />
              WhatsApp Story Ad
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
              Create your story ad
            </h2>
            <p className="mt-1 text-sm text-gray-500 truncate">
              {property?.propertytitle || "Selected property"}
            </p>

            <p className="mt-6 mb-3 text-[11px] font-extrabold text-gray-400 uppercase tracking-[0.18em]">
              Choose a style
            </p>
            <div className="grid grid-cols-3 md:grid-cols-1 gap-2.5 md:gap-3">
              {PRESET_TEMPLATES.map((t) => {
                const active = selectedId === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    aria-pressed={active}
                    className="flex flex-col md:flex-row items-center md:items-center gap-2 md:gap-4 p-3 md:p-4 rounded-2xl border-2 text-center md:text-left transition-all duration-200 hover:-translate-y-0.5"
                    style={
                      active
                        ? { borderColor: t.color, backgroundColor: `${t.color}12`, boxShadow: `0 8px 20px ${t.color}30` }
                        : { borderColor: "#EEF0F2", backgroundColor: "#FAFBFC" }
                    }
                  >
                    <span
                      className="w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                      style={{ backgroundColor: active ? t.color : "#fff", color: active ? "#fff" : "#6B7280", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}
                    >
                      {t.icon}
                    </span>
                    <span className="min-w-0 md:flex-1">
                      <span className="block text-sm md:text-base font-extrabold text-gray-900">{t.name}</span>
                      <span className="block text-[10px] md:text-xs font-bold uppercase tracking-wider text-gray-400">{t.tag}</span>
                    </span>
                    <span
                      className="hidden md:flex w-5 h-5 rounded-full border-2 items-center justify-center flex-shrink-0"
                      style={active ? { backgroundColor: t.color, borderColor: t.color } : { borderColor: "#D1D5DB" }}
                    >
                      {active && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sticky action bar */}
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-5 py-4 sm:px-8 md:px-10 md:py-5 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex gap-3">
              <button
                onClick={handleDownload}
                disabled={busy}
                className="flex-1 h-12 rounded-xl bg-gray-900 text-white text-sm font-extrabold flex items-center justify-center gap-2 shadow-lg hover:bg-black active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? <><Spinner /> Preparing…</> : <><DownloadIcon fontSize="small" /> Download</>}
              </button>
              <button
                onClick={handleShare}
                disabled={busy}
                className="flex-1 h-12 rounded-xl bg-[#56C662] text-white text-sm font-extrabold flex items-center justify-center gap-2 shadow-lg shadow-[#56C662]/30 hover:brightness-95 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSharing ? <><Spinner /> Sharing…</> : <><IosShareIcon fontSize="small" /> Share</>}
              </button>
            </div>
            <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
              1080 × 1920 · JPEG · WhatsApp &amp; Instagram
            </p>
          </div>
        </div>
      </div>

      {/* Off-screen render target for html2canvas */}
      <div aria-hidden="true" style={{ position: "fixed", left: "-9999px", top: 0, pointerEvents: "none" }}>
        <div ref={templateRef} style={{ width: 1080, height: 1920, overflow: "hidden" }}>
          {renderTemplate(selectedId)}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
}
